import Positionable from "./Positionable.mjs";
import Dimensionable from "./Dimensionable.mjs";
import Toggleable from "./Toggleable.mjs";

const edgeSpacing = 2; // The spacing of the popover to the window edges
const popupSpacing = 8;// The margin of the popup element to the root

export default {
    mixins: [Positionable, Dimensionable, Toggleable],

    props: {
        // The root element on which to position the popup element
        root: null,

        // Whether to position the popup element on the left after the alignment
        nudgeLeft: {
            type: Boolean,
            default: false
        },

        // Whether to position the popup element on the right after the alignment
        nudgeRight: {
            type: Boolean,
            default: false
        },

        // Whether the width of the popup element shall exactly be the same as the root
        attachWidth: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            hasWindow: false,
            activatorDimensions: {},
            popupDimensions: {}
        }
    },

    computed: {
        rootElement() {
            return this.root ? this.root : this.$el;
        },

        // Whether the popup element is located on the same hierarchy as the activator
        // Is overridden in Dropdown for hover activation
        hasRelativePosition() {
            return false;
        },

        popupPlacement() {
            if (this.left) {
                return 'left';
            }
            if (this.right) {
                return 'right';
            }

            // The popover will be displayed on top of the root, if requested or there is not enough space below
            const displayOnTop = this.top || (this.activatorDimensions.distance_to_bottom_window < this.activatorDimensions.distance_to_top_window && (this.activatorDimensions.distance_to_bottom_window < (popupSpacing + edgeSpacing + this.popupDimensions.height)))
            if (displayOnTop) {
                return 'top';
            }

            return 'bottom';
        },

        popupStyle() {
            const spacing = !popupSpacing ? 0 : popupSpacing;
            const style = {
                width: this.widthStyle,
                maxWidth: this.attachWidth ? (this.maxWidthStyle ? this.maxWidthStyle : (this.activatorDimensions.width + 'px')) : this.maxWidthStyle,
                position: 'absolute',
                zIndex: 2002 // we want to allow popups on alerts (z-index 2001)
            };

            // Left center
            if (this.popupPlacement === 'left') {
                const hasEnoughSpace = this.activatorDimensions.distance_to_left_window >= (popupSpacing + edgeSpacing + this.popupDimensions.width)

                return {
                    ...style,
                    top: (this.activatorDimensions.top - (this.popupDimensions.height / 2) + (this.activatorDimensions.height / 2)) + 'px',
                    right: hasEnoughSpace ? this.activatorDimensions.right + this.activatorDimensions.width + 'px' : undefined,
                    left: hasEnoughSpace ? undefined : `${spacing}px`,
                    paddingRight: `${spacing}px`, // Required for activation on hover to not hide the popup when moving the mouse from activator to popup
                    minWidth: this.minWidthStyle
                }
            }

            // Right center
            if (this.popupPlacement === 'right') {
                const hasEnoughSpace = this.activatorDimensions.distance_to_right_window >= (popupSpacing + edgeSpacing + this.popupDimensions.width)

                return {
                    ...style,
                    top: (this.activatorDimensions.top - (this.popupDimensions.height / 2) + (this.activatorDimensions.height / 2)) + 'px',
                    left: hasEnoughSpace ? (this.activatorDimensions.left + this.activatorDimensions.width + 'px') : undefined,
                    paddingLeft: `${spacing}px`, // Required for activation on hover to not hide the popup when moving the mouse from activator to popup
                    right: hasEnoughSpace ? undefined : `${spacing}px`,
                    minWidth: this.minWidthStyle
                }
            }

            // Calculate x-axis style for vertical positions
            if (this.nudgeRight) {
                style.right = this.hasRelativePosition ? 0 : Math.max(edgeSpacing, this.activatorDimensions.right);

                // If the popover element overflows on the left window screen, we shift it further to the right, even if the popover is not aligned as specified anymore
                const overflowOnLeft = -(this.activatorDimensions.left + this.activatorDimensions.width - this.popupDimensions.width - edgeSpacing)
                if (overflowOnLeft && overflowOnLeft > 0) {
                    style.right -= overflowOnLeft
                }

                style.right += 'px';
            } else {
                if (this.nudgeLeft) {
                    style.left = this.hasRelativePosition ? 0 : Math.max(edgeSpacing, this.activatorDimensions.left);
                } else {
                    // Center
                    style.left = this.hasRelativePosition ? ((this.activatorDimensions.width - this.popupDimensions.width) / 2) : Math.max(edgeSpacing, this.activatorDimensions.left - (this.popupDimensions.width / 2) + (this.activatorDimensions.width / 2));
                }

                // If the popover element overflows on the right window screen, we shift it further to the left, even if the popover is not positioned as specified anymore
                const overflowOnRight = style.left + this.popupDimensions.width - this.activatorDimensions.screen_width + edgeSpacing
                if (overflowOnRight && overflowOnRight > 0) {
                    style.left -= overflowOnRight
                } else {
                    // If the popover element overflows on the left window screen, we shift it further to the right, even if the popover is not positioned as specified anymore
                    const overflowOnLeft = -(style.left + this.activatorDimensions.left - edgeSpacing)
                    if (overflowOnLeft && overflowOnLeft > 0) {
                        style.left += overflowOnLeft
                    }
                }

                style.left += 'px';
            }

            style.minWidth = this.attachWidth && (!this.minWidth || !isNaN(this.minWidth)) ? (Math.max(this.activatorDimensions.width, this.minWidth ? this.minWidth : 0) + 'px') : this.minWidthStyle;


            if (this.popupPlacement === 'top') {
                if (this.hasRelativePosition) {
                    return {
                        ...style,
                        bottom: this.activatorDimensions.height + 'px',
                        paddingBottom: `${spacing}px`// Required for activation on hover to not hide the popup when moving the mouse from activator to popup
                    }
                }
                return {
                    ...style,
                    top: this.activatorDimensions.top - this.popupDimensions.height + 'px',
                    paddingBottom: `${spacing}px`// Required for activation on hover to not hide the popup when moving the mouse from activator to popup
                }
            }

            return {
                ...style,
                top: (this.hasRelativePosition ? 0 : this.activatorDimensions.top) + this.activatorDimensions.height + 'px',
                paddingTop: `${spacing}px`// Required for activation on hover to not hide the popup when moving the mouse from activator to popup
            };

        }
    },

    watch: {
        isActive(active) {
            if (this.shallUpdatePopupDimensions()) {
                this.callActivate();
            }

            if (active) {
                window.addEventListener('resize', this.onResize)
                window.addEventListener('scroll', this.onResize)
            } else {
                window.removeEventListener('resize', this.onResize)
                window.removeEventListener('scroll', this.onResize)
            }
        },
    },

    beforeMount() {
        this.hasWindow = typeof window !== 'undefined'
    },

    beforeUnmount() {
        this.isActive = false;
    },

    mounted() {
        this.isActive && this.callActivate()
    },

    methods: {
        shallUpdatePopupDimensions() {
            return !!this.isActive;
        },

        callActivate() {
            if (!this.hasWindow) return

            this.activate()
        },

        activate() {
            // Update coordinates and dimensions of popup
            // and its activator
            this.updateDimensions()
        },

        updateDimensions() {
            const rootBounding = this.rootElement.getBoundingClientRect();

            const scrollY = window.pageYOffset;
            const left = rootBounding.left;
            const right = document.body.clientWidth - rootBounding.right;
            const bottom = rootBounding.bottom + scrollY;
            const top = rootBounding.top + scrollY;

            const width = this.rootElement.clientWidth;
            const height = this.rootElement.clientHeight;

            this.activatorDimensions = {
                screen_width: document.body.clientWidth, // Without scrollbar (window.innerWidth weirdly contains scrollbar)
                distance_to_top_window: rootBounding.y,
                distance_to_bottom_window: window.innerHeight - (rootBounding.y + height),
                distance_to_right_window: window.innerWidth - (rootBounding.x + width),
                distance_to_left_window: rootBounding.x,
                width: width,
                height: height,
                left: left,
                right: right,
                top: top,
                bottom: bottom
            }

            // We need to wait for the next render to make sure the popup is attached to the dom
            setTimeout(() => {
                this.popupDimensions = {
                    width: this.$refs.popup ? this.$refs.popup.clientWidth : 0,
                    height: this.$refs.popup ? this.$refs.popup.clientHeight : 0,
                }
            }, 0);
        },

        onResize() {
            if (!this.isActive) return

            this.updateDimensions()
        },
    }
}
